import type { ManifestLayer } from "../types/contract";
import { assembleFertLayers, type RecInputs } from "./fertility";
import {
  DEFAULT_TUNABLES,
  optsFor,
  withTunables,
  type Base,
  type RxTunables,
} from "./rxInputs";

export interface TraditionalSurfaces {
  ph: (number | null)[] | null;
  p: (number | null)[] | null;
  k: (number | null)[] | null;
  cec: (number | null)[] | null;
}

/*
 *
 * Traditional build-and-maintain on kriged sample surfaces. The session still fetches and
 * kriges; this is the rec itself, the same engine a preset card uses, with genius credits off
 * and every season forced onto a flat rate.
 */
export function traditionalRxLayer(args: {
  base: Base;
  nutrient: "lime" | "k2o" | "p2o5";
  opened: RxTunables;
  surfaces: TraditionalSurfaces;
}): ManifestLayer | null {
  const { base, nutrient, opened, surfaces } = args;
  const soil: RecInputs = {
    ...base.soil,
    ph: surfaces.ph ?? base.soil.ph,
    p: surfaces.p ?? base.soil.p,
    k: surfaces.k ?? base.soil.k,
    cec: surfaces.cec ?? base.soil.cec,
    // traditional = plain Tri-State on sampled soil: no genius credits, no Albrecht
    kFixEff: undefined,
    pMinCredit: undefined,
    pRaw: undefined,
    kRaw: undefined,
    k2oAlbrecht: false,
    limeAlbrecht: false,
  };
  // FLAT-RATE REMOVAL: a traditional program spreads a constant maintenance rate field-wide —
  // keep the session's rotation CROPS but force every season onto the flat basis (the per-crop
  // preset constant), never the variable yield maps.
  const t: RxTunables = {
    ...DEFAULT_TUNABLES,
    seasons: opened.seasons.map((s) => ({
      crop: s.crop,
      basis: "flat" as const,
    })),
  };
  const soil2 = withTunables(soil, t, false, nutrient, base);
  const { layers } = assembleFertLayers(
    base.geoms,
    soil2,
    optsFor(nutrient, t, base),
  );
  const wantId =
    nutrient === "lime"
      ? "fert-lime"
      : nutrient === "p2o5"
        ? "fert-p2o5"
        : "fert-k2o";
  const l = layers.find((x) => x.id === wantId) ?? layers[0];
  if (!l) return null;
  const label =
    nutrient === "lime" ? "Lime" : nutrient === "p2o5" ? "P₂O₅" : "K₂O";
  return {
    ...l,
    id: `trad-rx-${nutrient}`,
    title: `Traditional ${label} Rx — kriged samples`,
    default_visible: true,
  };
}
